import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Sun, Menu, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { IMAGES } from "@/lib/content";

export default function Navbar() {
  const { t, theme, toggleTheme, lang, toggleLang } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "#home", label: t.nav.home, key: "home" },
    { id: "#services", label: t.nav.services, key: "services" },
    { id: "#about", label: t.nav.about, key: "about" },
    { id: "#areas", label: t.nav.areas, key: "areas" },
    { id: "#contact", label: t.nav.contact, key: "contact" },
  ];

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "py-3 bg-[hsl(var(--background))]/75 backdrop-blur-xl border-b border-border"
          : "py-5 bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-6 md:px-12 flex items-center justify-between">
        <a href="#home" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={IMAGES.ibkLogo} alt="Immobilienbetreuung Kern Logo" className="h-9 w-auto md:h-11 transition-transform duration-500 group-hover:scale-105" />
          <span className="hidden sm:block leading-none">
            <span className="block font-serif text-lg font-semibold tracking-tight text-foreground">Immobilienbetreuung</span>
            <span className="block text-[10px] tracking-[0.35em] uppercase text-muted-foreground">Kern · Neuffen</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={`nav-${l.key}`}
              className="relative text-sm font-medium text-foreground/80 hover:text-foreground transition-colors duration-300 py-1 group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-ibk transition-all duration-400 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="h-10 px-3 rounded-full text-xs font-bold tracking-widest uppercase text-foreground/80 hover:text-ibk hover:bg-foreground/5 transition-colors duration-300"
          >
            {lang === "de" ? "EN" : "DE"}
          </button>
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="h-10 w-10 grid place-items-center rounded-full text-foreground/80 hover:text-ibk hover:bg-foreground/5 transition-colors duration-300"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="lg:hidden h-10 w-10 grid place-items-center rounded-full text-foreground/80 hover:text-ibk hover:bg-foreground/5 transition-colors duration-300"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-[hsl(var(--background))]/95 backdrop-blur-xl border-t border-border mt-3"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.id}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.key}`}
                  className="font-serif text-2xl py-2 text-foreground hover:text-ibk transition-colors"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
